<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button :aria-label="$t('modals.filters')" @click="onFilterModal">
            <ion-icon slot="icon-only" :icon="icons.filterOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-buttons slot="end">
          <ion-button @click="dismiss">
            <ion-icon slot="icon-only" :icon="icons.close"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>
          {{ $t('modals.procedures') }}
        </ion-title>
      </ion-toolbar>
      <ion-toolbar>
        <ion-searchbar
          v-model="searchText"
          :placeholder="$t('fields.search.label')"
          @ionInput="onSearch"
          :debounce="300"
        ></ion-searchbar>
      </ion-toolbar>
      <ion-progress-bar v-if="loading" type="indeterminate"></ion-progress-bar>
    </ion-header>

    <ion-content :fullscreen="true">
      <ion-refresher slot="fixed" @ionRefresh="onRefresh">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>
      <ion-list>
        <ion-item
          v-for="procedure in procedures"
          :key="procedure.id"
          button
          @click="onProcedureModal(procedure)"
        >
          <ion-icon :icon="icons.helpBuoyOutline" slot="start"></ion-icon>
          <ion-label class="ion-text-wrap">
            <h2>{{ procedure.name }}</h2>
            <p>{{ formatDate(procedure.created) }}</p>
          </ion-label>
        </ion-item>
      </ion-list>

      <ODNNoContent v-if="procedures.length === 0 && loading === false" />

      <ion-infinite-scroll
        @ionInfinite="fetchMoreData"
        threshold="100px"
        :disabled="disableInfiniteScrolling"
      >
        <ion-infinite-scroll-content
          loading-spinner="bubbles"
          :loading-text="$t('messages.loading')"
        >
        </ion-infinite-scroll-content>
      </ion-infinite-scroll>
    </ion-content>
  </ion-page>
</template>

<script>
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonButtons,
  IonIcon,
  IonLabel,
  IonList,
  IonItem,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSearchbar,
  IonProgressBar,
  modalController,
  toastController,
} from '@ionic/vue';
import { close, filterOutline, helpBuoyOutline } from 'ionicons/icons';

import ODNModalProjectProceduresFilters from '@m/odn-modal-project-procedures-filters.vue';
import ODNModalProcedure from '@m/odn-modal-procedure.vue';
import ODNNoContent from '@c/odn-no-content.vue';

import APIService from '@s/api.service';

export default {
  name: 'ModalProjectProcedures',
  components: {
    IonContent,
    IonHeader,
    IonPage,
    IonTitle,
    IonToolbar,
    IonButton,
    IonButtons,
    IonIcon,
    IonLabel,
    IonList,
    IonItem,
    IonRefresher,
    IonRefresherContent,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    IonSearchbar,
    IonProgressBar,
    ODNNoContent,
  },
  props: {
    projectId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      procedures: [],
      filters: {
        sort: 'procedure.created',
        direction: 'desc',
      },
      page: 1,
      limit: 20,
      searchText: '',
      loading: true,
      disableInfiniteScrolling: true,
      icons: {
        close,
        filterOutline,
        helpBuoyOutline,
      },
    };
  },
  created() {
    this.fetchData();
  },
  methods: {
    formatDate(dt) {
      return this.$dayjs(dt).locale(this.$i18n.locale).format('ll');
    },
    async fetchData(clear = false) {
      try {
        this.loading = true;

        if (clear) {
          this.page = 1;
        }

        const response = await APIService.get(
          `/projects/${this.projectId}/procedures`,
          {
            page: this.page,
            limit: this.limit,
            search: this.searchText,
            sort: this.filters.sort,
            direction: this.filters.direction,
          }
        );

        if (clear) {
          this.procedures = [];
        }

        this.procedures = this.procedures.concat(response.data.items);

        if (this.procedures.length < response.data.meta.totalItems) {
          this.disableInfiniteScrolling = false;
        } else {
          this.disableInfiniteScrolling = true;
        }
      } catch (err) {
        const toast = await toastController.create({
          message: this.$t('messages.procedures.get.error'),
          color: 'danger',
          duration: 2000,
        });
        return toast.present();
      } finally {
        this.loading = false;
      }
    },
    async fetchMoreData(event) {
      this.page++;
      await this.fetchData();
      event.target.complete();
    },
    async onSearch() {
      await this.fetchData(true);
    },
    async onRefresh(event) {
      await this.fetchData(true);
      event.target.complete();
    },
    async onFilterModal() {
      const modal = await modalController.create({
        component: ODNModalProjectProceduresFilters,
        componentProps: {
          filters: this.filters,
        },
      });
      await modal.present();

      const newFilters = (await modal.onDidDismiss()).data;

      if (newFilters) {
        this.filters = newFilters;
        this.fetchData(true);
      }
    },
    async onProcedureModal(procedure) {
      const modal = await modalController.create({
        component: ODNModalProcedure,
        componentProps: {
          procedureId: procedure.id,
        },
      });
      await modal.present();
    },
    dismiss() {
      modalController.dismiss();
    },
  },
};
</script>
